import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { IconButton } from '@liveauctioneers/hammer-ui-core/iconButton';
import { Close, Warning } from '@liveauctioneers/hammer-ui-core/icons';
import { H4 } from '@liveauctioneers/hammer-ui-core/text';
import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { ExternalRouteButton } from '@/components/RouteButton/RouteButton';
import { NotificationPermissions } from '@/enums/notificationPermissions.enum';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getCookie, setCookie } from '@/redux/modules/cookies';
import {
    forceEnableNotificationsBannerDismiss,
    getForceEnableBanner,
    getPermission,
    requestBrowserPermission,
} from '@/redux/modules/notification';
import { useTrackBrowserNotificationsClicked } from '@/utils/amplitude/analytics/notifications';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';
import { enableBrowserNotificationsUrl } from '@/utils/urls';

const COOKIE_MAX_AGE = 86400 * 365; // 365 days
const COOKIE_NAME = 'dismissableNotificationBanner';
const COOKIE_BODY = 'dismissed';

export const DismissableGetWebNotificationsBanner = () => {
    const dispatch = useAppDispatch();
    const formatMessage = useAppFormatMessage();
    const { pathname } = useLocation();
    const isCheckoutPage = pathname.includes('/checkout/');
    const biddingConsole = pathname.includes('/console/');
    const forceEnableBanner = useAppSelector(getForceEnableBanner);
    const permission = useAppSelector(getPermission);
    const dismissed = useAppSelector((state) => getCookie(state, COOKIE_NAME)) === COOKIE_BODY;
    const trackBrowserNotificationsClicked = useTrackBrowserNotificationsClicked();

    const setCookieChecked = useCallback((): void => {
        dispatch(
            setCookie({
                maxAge: COOKIE_MAX_AGE,
                name: COOKIE_NAME,
                value: COOKIE_BODY,
            })
        );
        dispatch(forceEnableNotificationsBannerDismiss());
    }, [dispatch]);

    const onClickEnableNotifications = () => {
        dispatch(requestBrowserPermission());
        trackBrowserNotificationsClicked();
    };

    if (biddingConsole || isCheckoutPage || permission === '') {
        return null;
    }

    if (!forceEnableBanner) {
        if (permission === NotificationPermissions.Granted || Boolean(dismissed)) {
            return null;
        }
    }

    const disallowedNotifications = permission === NotificationPermissions.Denied;

    return (
        <div
            className="max-[975px]:pr-[40px] sticky top-0 z-[1000] flex h-[62px] w-full bg-primary-400 px-[20px] xsMax:hidden"
            data-testid="webNotificationsBanner"
            id="webNotificationsBanner"
        >
            {disallowedNotifications ? (
                <div
                    className="flex w-full items-center justify-center"
                    data-testid="webNotificationsDenied"
                    id="webNotificationsDenied"
                >
                    <Warning
                        className="mr-xs text-warning-400"
                        size="sm"
                        style="fill"
                    />
                    <H4 color="inverse">{formatMessage(['notifications', 'modify_your_browser_preferences'])}</H4>
                    <ExternalRouteButton
                        rel="noopener"
                        size="sm"
                        style="inverse"
                        target="_blank"
                        to={enableBrowserNotificationsUrl}
                        variant="outlined"
                    >
                        {formatMessage(['top_learn_more'])}
                    </ExternalRouteButton>
                </div>
            ) : (
                <div
                    className="flex w-full items-center justify-center"
                    data-testid="webNotificationsDefault"
                    id="webNotificationsDefault"
                >
                    <H4 color="inverse">{formatMessage(['notifications', 'get_notifications_when_outbid'])}</H4>
                    <Button
                        onClick={onClickEnableNotifications}
                        size="sm"
                        style="inverse"
                        variant="outlined"
                    >
                        {formatMessage(['notifications', 'enable_browser_notifications'])}
                    </Button>
                </div>
            )}
            <IconButton
                className="absolute right-xs top-xs z-10"
                icon={Close}
                onClick={setCookieChecked}
                size="sm"
                style="inverse"
            />
        </div>
    );
};
